import React from "react";
import { LegalPageLayout, LegalSection } from "../../components/LegalPageLayout";

const sections = [
  { id: "overview", label: "Overview" },
  { id: "info-we-access", label: "Information we access" },
  { id: "tesla-oauth", label: "How Tesla OAuth works" },
  { id: "storage", label: "What we store (and what we don't)" },
  { id: "encryption", label: "Encryption & security" },
  { id: "sharing", label: "Sharing & third parties" },
  { id: "cookies-tracking", label: "Cookies & tracking" },
  { id: "sms-communications", label: "SMS communications" },
  { id: "your-rights", label: "Your rights & choices" },
  { id: "retention", label: "Data retention" },
  { id: "children", label: "Children's privacy" },
  { id: "changes", label: "Changes to this policy" },
  { id: "contact", label: "Contact us" },
];

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      eyebrow="Legal · 01"
      title="Privacy Policy"
      updated="February 20, 2026"
      intro="EVIQ is an independent battery-intelligence platform. This policy explains what information we access when you use EVIQ, how Tesla OAuth is used, what we store (and don't), how information is encrypted, and how you stay in control of your data."
      sections={sections}
    >
      <LegalSection id="overview" title="1. Overview">
        <p>
          This Privacy Policy describes how EVIQ (&ldquo;<strong>EVIQ</strong>,&rdquo;
          &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>&rdquo;) collects,
          uses, protects, and shares information when you visit our website at{" "}
          <a href="https://eviq.pro">eviq.pro</a>, use our mobile web application at{" "}
          <a href="/app">/app</a>, or authorize EVIQ to read your Tesla vehicle telemetry
          through the official Tesla Fleet API.
        </p>
        <p>
          <strong>The short version:</strong> EVIQ does not store your Tesla vehicle
          information on our servers. Vehicle information is retrieved directly from
          Tesla through the official Tesla Fleet API after you authorize access via Tesla
          OAuth. You authenticate directly with Tesla — we never see or receive your Tesla
          password.
        </p>
      </LegalSection>

      <LegalSection id="info-we-access" title="2. Information we access">
        <p>The information EVIQ accesses falls into two categories:</p>
        <ul>
          <li>
            <strong>Account information you provide.</strong> Your email address, chosen
            plan (Basic Monthly / Yearly), region/state (for climate math), and — during
            paid checkout — billing details processed by our payment provider.
          </li>
          <li>
            <strong>Vehicle telemetry from Tesla</strong> (only after you sign in with
            Tesla). This is limited to the read-only scopes you approve during Tesla OAuth,
            typically:
            <ul>
              <li>Vehicle identifier (VIN, model, year, trim)</li>
              <li>State-of-charge and estimated range</li>
              <li>Battery-pack diagnostics needed to compute the EVIQ Score™</li>
              <li>Odometer reading</li>
              <li>Charging session metadata (start/stop, kWh, location if you grant it)</li>
            </ul>
          </li>
        </ul>
        <p>
          EVIQ has <strong>read-only</strong> access. EVIQ cannot unlock, drive, honk,
          summon, precondition, or otherwise control your vehicle. See our{" "}
          <a href="/security">Security</a> page for details.
        </p>
      </LegalSection>

      <LegalSection id="tesla-oauth" title="3. How Tesla OAuth works">
        <p>
          EVIQ uses <strong>Tesla OAuth 2.0</strong> — the same authorization mechanism
          Tesla uses for its own apps and its official third-party developer partners.
          When you click &ldquo;Sign in with Tesla&rdquo; in EVIQ, the following happens:
        </p>
        <ol>
          <li>
            You are redirected to Tesla&rsquo;s own authentication page at{" "}
            <code>auth.tesla.com</code>. EVIQ never sees your Tesla email or password.
          </li>
          <li>
            You authenticate directly with Tesla and choose which vehicles and which
            read-only scopes you wish to authorize EVIQ to access.
          </li>
          <li>
            Tesla returns a short-lived access token and refresh token to our OAuth
            callback endpoint at <code>eviq.pro/api/tesla/callback</code>.
          </li>
          <li>
            EVIQ uses that token to request vehicle data <strong>on demand</strong> from
            Tesla&rsquo;s Fleet API. You can revoke access at any time from{" "}
            <a href="https://accounts.tesla.com/en_US/account-settings/security" target="_blank" rel="noreferrer">
              your Tesla account
            </a>{" "}
            or from within EVIQ.
          </li>
        </ol>
      </LegalSection>

      <LegalSection id="storage" title="4. What we store (and what we don't)">
        <p>
          <strong>EVIQ does not store your Tesla vehicle information on our servers.</strong>{" "}
          Vehicle information is retrieved directly from Tesla through the official Tesla
          Fleet API after you authorize access via Tesla OAuth, and used only to render
          the analytics you requested during your session.
        </p>
        <p>What we <strong>do</strong> store, minimally, and only to make the service work:</p>
        <ul>
          <li>Your email address, plan, and subscription status.</li>
          <li>Your encrypted Tesla refresh token, so you don&rsquo;t have to re-authorize on every visit.</li>
          <li>Non-personal aggregate telemetry (e.g., total packs monitored) used purely for platform statistics on our home page. No VIN, no identifier, no address.</li>
          <li>Standard server logs (IP address, timestamp, HTTP path) retained for a limited time for security and debugging.</li>
        </ul>
        <p>What we <strong>do not</strong> store:</p>
        <ul>
          <li>Your Tesla password (Tesla never shares it with EVIQ).</li>
          <li>Real-time vehicle telemetry after the request that used it.</li>
          <li>Location history or drive routes.</li>
          <li>Vehicle unlock, drive, or charge-control commands (EVIQ never sends these).</li>
        </ul>
      </LegalSection>

      <LegalSection id="encryption" title="5. Encryption & security">
        <p>
          All traffic between your browser, EVIQ, and Tesla is encrypted in transit using{" "}
          <strong>TLS 1.2 or higher (HTTPS)</strong>. Refresh tokens are encrypted at rest
          using AES-256 before being written to our database. Our database is hosted with
          a SOC 2-audited cloud provider with encryption-at-rest enabled at the disk
          layer as well.
        </p>
        <p>
          Payment card details are handled by our PCI-DSS-compliant payment processor
          (Stripe). EVIQ&rsquo;s servers never see, store, or transmit your card number,
          CVC, or expiration date.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="6. Sharing & third parties">
        <p>
          EVIQ does <strong>not</strong> sell, rent, or trade your personal information.
          We share information only with the small set of service providers required to
          operate EVIQ, and only the minimum needed for each purpose:
        </p>
        <ul>
          <li><strong>Tesla, Inc.</strong> — to fetch the vehicle data you authorized.</li>
          <li><strong>Stripe, Inc.</strong> — to process your subscription payment.</li>
          <li><strong>MongoDB Atlas</strong> — to store your account, subscription, and encrypted refresh token.</li>
          <li><strong>Anthropic</strong> — for the AI narrative generation used inside EVIQ Intelligence™. Prompts sent to Anthropic contain only the computed metrics you see on-screen (score, degradation curve, warranty ETA); they do <em>not</em> contain your email, VIN, or Tesla token.</li>
          <li><strong>Resend</strong> — to deliver transactional emails such as your battery report PDF and receipts.</li>
        </ul>
        <p>
          We may disclose information if required by law, a valid subpoena, or to protect
          EVIQ, our users, or the public from fraud, abuse, or imminent harm.
        </p>
      </LegalSection>

      <LegalSection id="cookies-tracking" title="7. Cookies & tracking">
        <p>
          EVIQ uses a small number of first-party cookies that keep you signed in and
          remember your preferences (e.g., selected vehicle, language). See our{" "}
          <a href="/cookies">Cookie Policy</a> for the full list, including any analytics
          or marketing pixels currently in use.
        </p>
      </LegalSection>

      <LegalSection id="sms-communications" title="8. SMS communications (text alerts)">
        <p>
          SMS text alerts are <strong>opt-in only</strong> and are used exclusively to send
          transactional, account-related battery-health notifications (e.g., warranty-threshold
          risk, charging anomaly, weekly report). We never send marketing or promotional
          messages by SMS.
        </p>
        <p>
          To enable alerts, you must (a) enter your mobile number inside the EVIQ app,
          (b) tick a required consent checkbox showing the exact disclosure copy, and
          (c) confirm a 6-digit verification code we send you. We record your consent
          timestamp, IP, phone number, and the exact copy shown at that moment for our
          audit trail. Message frequency is capped at <strong>4 messages per month</strong>.
          Message &amp; data rates may apply.
        </p>
        <p>
          You can revoke consent at any time by (i) tapping <em>Unsubscribe</em> on the
          Alerts screen or (ii) replying <strong>STOP</strong> to any EVIQ SMS. Reply
          <strong> HELP </strong> to receive support instructions. Revocation is honored
          immediately; the consent record itself is retained for regulatory audit purposes.
          SMS is delivered by our third-party provider (Twilio) under a strictly transactional
          10DLC campaign — see{" "}
          <a href="/terms#sms-alerts">Terms &sect;9</a> for the full messaging agreement.
        </p>
      </LegalSection>

      <LegalSection id="your-rights" title="9. Your rights & choices">
        <p>Depending on where you live, you may have the right to:</p>
        <ul>
          <li>Access the personal information EVIQ holds about you.</li>
          <li>Correct information that&rsquo;s inaccurate or out of date.</li>
          <li>Delete your account and associated data.</li>
          <li>Export your account data in a portable format.</li>
          <li>Withdraw consent for Tesla OAuth (which immediately revokes our access).</li>
          <li>Opt out of the &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of personal information under CCPA/CPRA. (EVIQ does not sell personal information — but you have the right regardless.)</li>
        </ul>
        <p>
          Email <a href="mailto:support@eviq.pro">support@eviq.pro</a> to exercise any
          of these rights. We&rsquo;ll respond within 30 days.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="10. Data retention">
        <p>
          We retain account and subscription data for as long as your account is active.
          When you close your account, we delete or anonymize your personal information
          within 90 days, except where a longer retention period is required by law (for
          example, tax records for paid subscriptions).
        </p>
      </LegalSection>

      <LegalSection id="children" title="11. Children's privacy">
        <p>
          EVIQ is not directed to children under 16 and we do not knowingly collect
          personal information from them. If you believe a child has provided us
          information, please contact us and we&rsquo;ll delete it.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="12. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be
          announced by email and on this page. The &ldquo;Last updated&rdquo; date at the
          top always reflects the current version.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="13. Contact us">
        <p>
          Questions about this policy or your data? Email us at{" "}
          <a href="mailto:support@eviq.pro">support@eviq.pro</a> — we typically
          respond within 1–2 business days.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
